import { z } from 'zod';

// ============================================================
// MAIL-ACCOUNT (Multi-Sender)
// ============================================================

/**
 * Zod Schema für ein Mail-Konto (MailAccount).
 * Repräsentiert ein Google-OAuth-Konto das als Absender genutzt werden kann.
 * OAuth-Credentials werden über den credentialKey aus Cloudflare Secrets geladen.
 */
export const MailAccountSchema = z.object({
  /** Eindeutige ID des Mail-Kontos (CUID) */
  id: z.string().cuid(),
  /** Anzeigename, z.B. "Verein" oder "Glenn (persönlich)" */
  name: z.string().min(1),
  /** E-Mail-Adresse des Absenders */
  email: z.string().email(),
  /** Ob dies der Standard-Account ist */
  isDefault: z.boolean().default(false),
  /** Ob der Account aktiv und verwendbar ist */
  isActive: z.boolean().default(true),
  /**
   * Name der Cloudflare-Secret-Environment-Variable.
   * Wert muss JSON sein: { client_id, client_secret, refresh_token }
   */
  credentialKey: z.string().min(1),
  /**
   * Optionale HTML-Signatur dieses Absenders.
   * Wird beim Mailversand automatisch als {{signature}} Variable eingefügt.
   */
  signature: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** TypeScript Type für ein Mail-Konto */
export type MailAccount = z.infer<typeof MailAccountSchema>;

/**
 * Schema für die Erstellung eines Mail-Kontos.
 */
export const CreateMailAccountSchema = MailAccountSchema.pick({
  name: true,
  email: true,
  isDefault: true,
  isActive: true,
  credentialKey: true,
  signature: true,
});

/** TypeScript Type für Mail-Konto-Erstellung */
export type CreateMailAccount = z.infer<typeof CreateMailAccountSchema>;

/**
 * Schema für das Aktualisieren eines Mail-Kontos (PATCH-Semantik).
 */
export const UpdateMailAccountSchema = CreateMailAccountSchema.partial();

/** TypeScript Type für Mail-Konto-Update */
export type UpdateMailAccount = z.infer<typeof UpdateMailAccountSchema>;

/**
 * Antwort-Schema für GET /mail/accounts.
 */
export const MailAccountListResponseSchema = z.object({
  /** Liste aller Mail-Konten */
  data: z.array(MailAccountSchema),
  /** Gesamtanzahl */
  total: z.number().int().nonnegative(),
});

/** TypeScript Type für Mail-Konto-Listen-Antwort */
export type MailAccountListResponse = z.infer<typeof MailAccountListResponseSchema>;

// ============================================================

/**
 * Schema für eine konfigurierbare Absender-Adresse.
 * Wird in der Setting-Tabelle unter `mail.sender.addresses` als JSON-Array gespeichert.
 */
export const MailSenderAddressSchema = z.object({
  /** Anzeigename des Absenders, z.B. "Glenn Odya" */
  name: z.string().min(1),
  /** E-Mail-Adresse des Absenders, z.B. "glenn@fanini.live" */
  email: z.string().email(),
  /** Bezeichnung zur Auswahl im UI, z.B. "Vorsitzender" */
  label: z.string().min(1),
  /**
   * Optionale HTML-Signatur des Absenders.
   * Wird beim Mailversand an den Footer angehängt.
   * HTML ist erlaubt; der Inhalt wird unverändert eingefügt.
   */
  signature: z.string().optional(),
});

/** TypeScript Type für eine Absender-Adresse */
export type MailSenderAddress = z.infer<typeof MailSenderAddressSchema>;

/**
 * Response-Schema für GET /settings/mail-senders.
 */
export const MailSendersResponseSchema = z.object({
  /** Liste aller konfigurierten Absender-Adressen */
  addresses: z.array(MailSenderAddressSchema),
  /** E-Mail der Standard-Absenderadresse (leerer String wenn nicht gesetzt) */
  defaultEmail: z.string(),
});

/** TypeScript Type für die Mail-Senders-Response */
export type MailSendersResponse = z.infer<typeof MailSendersResponseSchema>;

/**
 * Mail-Log Status Enum.
 */
export const MailLogStatusSchema = z.enum(['sent', 'failed', 'bounced']);

/** TypeScript Type für Mail-Log Status */
export type MailLogStatus = z.infer<typeof MailLogStatusSchema>;

/**
 * Zod Schema für MailTemplate — Runtime-Validierung + Type-Inference.
 * 4 Scope-Ebenen: GLOBAL (kein category/season/event) > CATEGORY > SEASON > EVENT-spezifisch.
 */
export const MailTemplateSchema = z.object({
  id: z.string().cuid(),
  slug: z.string(), // z.B. "registration_confirmation", "ticket_assigned"
  name: z.string(),
  subject: z.string(),
  bodyHtml: z.string(),
  bodyText: z.string().nullable().optional(),
  categoryId: z.string().cuid().nullable().optional(),
  /** Season-Scope: Template gilt für alle Events dieser Season */
  seasonId: z.string().cuid().nullable().optional(),
  eventId: z.string().cuid().nullable().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type MailTemplate = z.infer<typeof MailTemplateSchema>;

/**
 * Schema für MailTemplate-Erstellung.
 */
export const CreateMailTemplateSchema = MailTemplateSchema.pick({
  slug: true,
  name: true,
  subject: true,
  bodyHtml: true,
  bodyText: true,
  categoryId: true,
  seasonId: true,
  eventId: true,
  isActive: true,
});

/** TypeScript Type für MailTemplate-Erstellung */
export type CreateMailTemplate = z.infer<typeof CreateMailTemplateSchema>;

/**
 * Schema für MailTemplate-Update.
 * Alle Felder optional — PATCH-Semantik.
 */
export const UpdateMailTemplateSchema = CreateMailTemplateSchema.partial();

/** TypeScript Type für MailTemplate-Update */
export type UpdateMailTemplate = z.infer<typeof UpdateMailTemplateSchema>;

/**
 * Zod Schema für MailLog — Runtime-Validierung + Type-Inference.
 * Sende-Historie für alle versendeten Mails.
 */
export const MailLogSchema = z.object({
  id: z.string().cuid(),
  userId: z.string(),
  toEmail: z.string().email(),
  subject: z.string(),
  templateSlug: z.string().nullable().optional(),
  status: MailLogStatusSchema.default('sent'),
  errorMessage: z.string().nullable().optional(),
  /**
   * Optionale Event-ID — verknüpft den Mail-Log-Eintrag mit einem Event.
   * Null für system-weite Mails (z.B. Test-Mails, einzelne Freitext-Mails ohne Event-Kontext).
   */
  eventId: z.string().nullable().optional(),
  /** Optionale EventMail-ID — verknüpft mit einer gesendeten Update-Mail. */
  eventMailId: z.string().nullable().optional(),
  sentAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type MailLog = z.infer<typeof MailLogSchema>;

/**
 * Schema für MailLog-Erstellung.
 */
export const CreateMailLogSchema = MailLogSchema.pick({
  userId: true,
  toEmail: true,
  subject: true,
  templateSlug: true,
  status: true,
  errorMessage: true,
  eventId: true,
  eventMailId: true,
});

/** TypeScript Type für MailLog-Erstellung */
export type CreateMailLog = z.infer<typeof CreateMailLogSchema>;

/**
 * Response-Schema für GET /mail/logs/counts?eventId=X.
 * Gibt pro userId die Anzahl an Mails zurück die für ein Event versendet wurden.
 *
 * @example
 * ```json
 * { "counts": { "user_abc": 3, "user_xyz": 1 } }
 * ```
 */
export const MailLogCountsResponseSchema = z.object({
  /** Map von userId → Anzahl versendeter Mails für das angeforderte Event */
  counts: z.record(z.string(), z.number().int().nonnegative()),
});

/** TypeScript Type für MailLog-Counts-Response */
export type MailLogCountsResponse = z.infer<typeof MailLogCountsResponseSchema>;

// ============================================================
// EVENT-MAIL (Update-Mails zu Events)
// ============================================================

/**
 * Zod Schema für EventMail — eine gesendete Update-Mail an alle Event-Teilnehmer.
 * Wird beim Versand über POST /events/:id/send-update erstellt.
 */
export const EventMailSchema = z.object({
  /** Eindeutige ID (CUID) */
  id: z.string().cuid(),
  /** Event-ID */
  eventId: z.string(),
  /** Betreff der Mail */
  subject: z.string(),
  /** HTML-Body der Mail */
  bodyHtml: z.string(),
  /** Typ der Mail (update, no_show, custom) */
  type: z.string().default('update'),
  /** Sendezeitpunkt */
  sentAt: z.coerce.date(),
  /** Clerk User ID des Senders */
  sentBy: z.string(),
  /** Anzahl der Empfänger */
  sentCount: z.number().int().nonnegative(),
});

/** TypeScript Type für eine EventMail */
export type EventMail = z.infer<typeof EventMailSchema>;

/**
 * Response-Schema für GET /events/:id/mails.
 * Liste aller gesendeten Update-Mails eines Events.
 */
export const EventMailListResponseSchema = z.object({
  /** Liste der gesendeten Mails */
  data: z.array(EventMailSchema),
  /** Gesamtanzahl */
  total: z.number().int().nonnegative(),
});

/** TypeScript Type für EventMail-Listen-Response */
export type EventMailListResponse = z.infer<typeof EventMailListResponseSchema>;

/**
 * Response-Schema für POST /events/:id/mails/:mailId/resend.
 * Gibt an wie viele Mails nachgesendet wurden.
 */
export const EventMailResendResponseSchema = z.object({
  /** Anzahl neu versendeter Mails (Differenz-Empfänger) */
  sent: z.number().int().nonnegative(),
  /** Fehler-Nachrichten für fehlgeschlagene Zustellungen */
  errors: z.array(z.string()),
  /** Anzahl bereits erhaltener Empfänger (übersprungen) */
  skipped: z.number().int().nonnegative(),
});

/** TypeScript Type für EventMail-Resend-Response */
export type EventMailResendResponse = z.infer<typeof EventMailResendResponseSchema>;

/**
 * Zod Schema für das Senden einer Update-Mail an alle Ticket-Inhaber eines Events.
 * Wird von POST /events/:id/send-update validiert.
 */
export const SendEventUpdateSchema = z.object({
  /** Betreff der Update-Mail */
  subject: z.string().min(1, 'Betreff darf nicht leer sein').max(200),
  /** Nachrichtentext der Update-Mail */
  body: z.string().min(1, 'Nachricht darf nicht leer sein').max(5000),
});

/** TypeScript Type für das Senden einer Event-Update-Mail */
export type SendEventUpdate = z.infer<typeof SendEventUpdateSchema>;

/**
 * Antwort-Schema von POST /events/:id/send-update.
 */
export const SendEventUpdateResponseSchema = z.object({
  /** Anzahl erfolgreich versendeter Mails */
  sent: z.number().int().nonnegative(),
  /** Fehler-Nachrichten für fehlgeschlagene Zustellungen */
  errors: z.array(z.string()),
});

/** TypeScript Type für die Antwort des send-update Endpunkts */
export type SendEventUpdateResponse = z.infer<typeof SendEventUpdateResponseSchema>;

/**
 * Schema für ein aufgelöstes Template pro Purpose.
 * Wird von GET /events/:eventId/resolved-templates zurückgegeben.
 * Enthält sowohl event-spezifische als auch globale Fallback-Templates.
 */
export const ResolvedTemplateSchema = z.object({
  /** Template-Purpose (z.B. 'ticket_assigned', 'approval', 'rejection') */
  purpose: z.string(),
  /** Ob ein event-spezifisches Template (Level: event) aktiv ist */
  hasEventOverride: z.boolean(),
  /** Scope-Ebene des verwendeten Templates (4-Level-Hierarchie) */
  source: z.enum(['event', 'season', 'category', 'global', 'none']),
  /** Das aufgelöste Template oder null wenn kein Template existiert */
  template: z
    .object({
      id: z.string(),
      slug: z.string(),
      name: z.string(),
      subject: z.string(),
      bodyHtml: z.string(),
    })
    .nullable(),
});

/** TypeScript Type für ein aufgelöstes Template */
export type ResolvedTemplate = z.infer<typeof ResolvedTemplateSchema>;

/**
 * Antwort-Schema von GET /events/:eventId/resolved-templates.
 */
export const ResolvedTemplatesResponseSchema = z.array(ResolvedTemplateSchema);

/** TypeScript Type für die Liste aufgelöster Templates */
export type ResolvedTemplatesResponse = z.infer<typeof ResolvedTemplatesResponseSchema>;

/**
 * Paginierte Antwort von GET /mail/logs.
 */
export const MailLogListResponseSchema = z.object({
  /** Liste der Mail-Log-Einträge */
  data: z.array(MailLogSchema),
  /** Gesamtanzahl der Einträge */
  total: z.number().int().nonnegative(),
  /** Angewendetes Limit */
  limit: z.number().int().positive(),
  /** Angewendeter Offset */
  offset: z.number().int().nonnegative(),
});

/** TypeScript Type für die paginierte MailLog-Antwort */
export type MailLogListResponse = z.infer<typeof MailLogListResponseSchema>;

/**
 * Schema für Erstellen/Aktualisieren eines event-spezifischen Templates.
 * Wird von PUT /events/:eventId/mail-templates/:slug validiert.
 */
export const UpsertEventMailTemplateSchema = z.object({
  /** Anzeigename des Templates */
  name: z.string().min(1).max(200),
  /** Betreff der Mail */
  subject: z.string().min(1, 'Betreff darf nicht leer sein').max(500),
  /** HTML-Body der Mail */
  bodyHtml: z.string().min(1, 'Body darf nicht leer sein'),
});

/** TypeScript Type für Upsert Event Mail Template */
export type UpsertEventMailTemplate = z.infer<typeof UpsertEventMailTemplateSchema>;

/**
 * Request-Body für POST /mail/send-single.
 * Sendet eine Freitext-Mail an einen einzelnen User.
 *
 * @example
 * ```ts
 * const result = SendSingleMailSchema.safeParse(body);
 * ```
 */
export const SendSingleMailSchema = z.object({
  /** Clerk User-ID des Empfängers (muss in DB existieren) */
  userId: z.string().min(1),
  /** Betreff der Mail */
  subject: z.string().min(1, 'Betreff darf nicht leer sein').max(200),
  /** Freitext-Nachricht (wird in <p>-Tags gewrapped) */
  message: z.string().min(1, 'Nachricht darf nicht leer sein').max(5000),
  /** Optionale Event-ID als Kontext (nur für MailLog, kein Einfluss auf Template) */
  eventId: z.string().min(1).optional(),
});

/** TypeScript Type für Einzel-Mail-Request */
export type SendSingleMail = z.infer<typeof SendSingleMailSchema>;

/**
 * Response-Schema für POST /mail/send-single.
 */
export const SendSingleMailResponseSchema = z.object({
  /** Ob der Versand erfolgreich war */
  success: z.boolean(),
  /** Gmail-Message-ID (wenn verfügbar) */
  messageId: z.string().optional(),
});

/** TypeScript Type für Einzel-Mail-Response */
export type SendSingleMailResponse = z.infer<typeof SendSingleMailResponseSchema>;

// ============================================================
// NO-SHOW MAIL
// ============================================================

/**
 * Ein No-Show-Empfänger — Fan der registriert war, aber nicht eingecheckt hat.
 */
export const NoShowRecipientSchema = z.object({
  /** Clerk User-ID */
  userId: z.string(),
  /** Anzeigename des Fans */
  displayName: z.string().nullable(),
  /** E-Mail-Adresse */
  email: z.string().email(),
});

/** TypeScript Type für einen No-Show-Empfänger */
export type NoShowRecipient = z.infer<typeof NoShowRecipientSchema>;

/**
 * Response-Schema für GET /events/:id/no-shows.
 */
export const NoShowsResponseSchema = z.object({
  /** Liste der No-Show-Fans */
  noShows: z.array(NoShowRecipientSchema),
  /** Gesamtanzahl der No-Shows */
  count: z.number().int().nonnegative(),
});

/** TypeScript Type für No-Shows-Response */
export type NoShowsResponse = z.infer<typeof NoShowsResponseSchema>;

/**
 * Request-Body für POST /events/:id/mail-no-shows.
 */
export const MailNoShowsRequestSchema = z.object({
  /**
   * Template-Slug (Standard: "no_show").
   * Wenn angegeben, wird das Template aus der Datenbank geladen.
   */
  templateSlug: z.string().optional(),
  /** Optionaler Betreff-Override */
  subject: z.string().max(200).optional(),
  /** Optionaler Body-Override (Freitext, wird in Template eingebettet) */
  body: z.string().max(10_000).optional(),
  /** Optionale Absender-E-Mail */
  mailAccountId: z.string().optional(),
});

/** TypeScript Type für No-Show-Mail-Request */
export type MailNoShowsRequest = z.infer<typeof MailNoShowsRequestSchema>;

/**
 * Response-Schema für POST /events/:id/mail-no-shows.
 */
export const MailNoShowsResponseSchema = z.object({
  /** Anzahl der Empfänger (No-Shows) */
  recipientCount: z.number().int().nonnegative(),
  /** Anzahl erfolgreich versendeter Mails */
  mailsSent: z.number().int().nonnegative(),
  /** Fehler-Nachrichten für fehlgeschlagene Zustellungen */
  errors: z.array(z.string()),
});

/** TypeScript Type für No-Show-Mail-Response */
export type MailNoShowsResponse = z.infer<typeof MailNoShowsResponseSchema>;
